import React, { useState, useEffect } from 'react';
import { 
  Activity, TrendingUp, TrendingDown, Award, Target, 
  CheckCircle, AlertTriangle, ArrowUp, ArrowDown, Info,
  Zap, Shield, Tag, Clock, BarChart3
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import BrandTitle from '../components/BrandTitle';

const CostHealthScore = () => {
  const { shouldShowData } = useDemoData();
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchHealthScore();
  }, []);

  const fetchHealthScore = async () => {
    try {
      const response = await fetch(`${API_URL}/api/health-score`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setHealthData(data);
    } catch (error) {
      console.error('Error fetching health score:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    if (grade.startsWith('A')) return 'text-green-500';
    if (grade === 'B') return 'text-emerald-500';
    if (grade === 'C') return 'text-yellow-500';
    return 'text-red-500';
  };

  // Map score → emerald (good) / amber (okay) / rose (poor).
  // Note: returns STATIC tailwind classes so JIT picks them up.
  const getScoreColorClasses = (score) => {
    if (score >= 80) return { ring: 'ring-emerald-200', bg: 'bg-emerald-500/10', text: 'text-emerald-300', accent: 'text-emerald-600' };
    if (score >= 60) return { ring: 'ring-emerald-200', bg: 'bg-emerald-500/10', text: 'text-emerald-300', accent: 'text-emerald-600' };
    if (score >= 40) return { ring: 'ring-amber-200', bg: 'bg-amber-500/10', text: 'text-amber-300', accent: 'text-amber-600' };
    return { ring: 'ring-rose-200', bg: 'bg-rose-500/10', text: 'text-rose-300', accent: 'text-rose-600' };
  };

  const getFactorIcon = (factor) => {
    const icons = {
      resource_utilization: Activity,
      reserved_coverage: Shield,
      tagging_compliance: Tag,
      anomaly_response: Clock,
      budget_accuracy: Target,
      waste_elimination: Zap
    };
    return icons[factor] || BarChart3;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="health-score-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!shouldShowData) return <EmptyDataState title="Cost Health Score" description="Connect your cloud accounts to generate your cloud cost health scorecard." />;
  const sc = getScoreColorClasses(healthData?.overall_score || 0);
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 -mt-px">
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="cost-health-score-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-white flex items-center gap-2">
            <Award className="w-7 h-7 text-emerald-400" />
            <BrandTitle>Cost Health Score</BrandTitle>
          </h1>
          <p className="text-slate-200 text-sm mt-1">
            Your cloud spending efficiency score — like a credit score for costs
          </p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-4 py-2 rounded-full text-sm font-medium">
          Unique to CARTIEAI
        </div>
      </div>

      {/* Main Score Card — v34 (Feb 2026): rebuilt to use STATIC tailwind classes.
          Previously `bg-gradient-to-r ${getScoreColor(...)}` failed because
          Tailwind JIT can't see dynamic class names, so the gradient never
          painted and white text on white background became invisible. */}
      <div className={`rounded-2xl shadow-sm overflow-hidden border ${sc.ring} ring-1 ${sc.bg}`}>
        <div className="p-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className={`text-xs font-bold tracking-widest uppercase ${sc.accent}`}>Your Cost Health Score</p>
              <div className="flex items-baseline gap-4 mt-2">
                <span className={`text-7xl font-extrabold tabular-nums ${sc.text}`} data-testid="health-score-value">
                  {healthData?.overall_score?.toFixed(0)}
                </span>
                <span className="text-3xl font-bold text-slate-400">/100</span>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <span className={`text-3xl font-extrabold ${getGradeColor(healthData?.grade || 'C')} bg-slate-900/60 border border-white/10 px-4 py-1 rounded-lg shadow-sm`}>
                  {healthData?.grade}
                </span>
                <span className="text-slate-200 text-lg font-medium">{healthData?.status}</span>
              </div>
            </div>
            <div className="text-right">
              <Award className={`w-20 h-20 ${sc.accent} opacity-40`} />
              <p className="text-slate-200 text-sm mt-2 font-medium">
                Top {healthData?.industry_comparison?.percentile}% in industry
              </p>
            </div>
          </div>
        </div>

        {/* Trend */}
        <div className="p-5 bg-white/60 border-t border-white/10 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {healthData?.trend?.change >= 0 ? (
              <ArrowUp className="w-5 h-5 text-emerald-500" />
            ) : (
              <ArrowDown className="w-5 h-5 text-rose-500" />
            )}
            <span className={`font-semibold ${healthData?.trend?.change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {healthData?.trend?.change >= 0 ? '+' : ''}{healthData?.trend?.change?.toFixed(1)} points
            </span>
            <span className="text-slate-300">vs last month</span>
          </div>
          <div className="text-slate-300">
            Industry Average: <span className="font-semibold text-white">{healthData?.industry_comparison?.industry_average}</span>
          </div>
        </div>
      </div>

      {/* Factor Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {healthData?.factors && Object.entries(healthData.factors).map(([key, factor]) => {
          const Icon = getFactorIcon(key);
          return (
            <div 
              key={key}
              className="bg-slate-900/60 dark:bg-gray-800 rounded-xl p-5 shadow-lg hover:shadow-xl transition-shadow"
              data-testid={`factor-${key}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${factor.score >= 80 ? 'bg-green-100 text-green-600' : factor.score >= 60 ? 'bg-emerald-500/15 text-emerald-600' : 'bg-yellow-100 text-yellow-600'}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="font-medium text-slate-200 dark:text-gray-300 capitalize">
                    {key.replace(/_/g, ' ')}
                  </span>
                </div>
                <span className={`text-2xl font-bold ${factor.score >= 80 ? 'text-green-500' : factor.score >= 60 ? 'text-emerald-500' : 'text-yellow-500'}`}>
                  {factor.score}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
                <div 
                  className={`h-2 rounded-full ${factor.score >= 80 ? 'bg-green-500' : factor.score >= 60 ? 'bg-emerald-500' : 'bg-yellow-500'}`}
                  style={{ width: `${factor.score}%` }}
                ></div>
              </div>
              <p className="text-sm text-slate-400 dark:text-gray-400">{factor.details}</p>
              <p className="text-xs text-gray-400 dark:text-slate-400 mt-1">{factor.benchmark}</p>
            </div>
          );
        })}
      </div>

      {/* Improvement Actions */}
      <div className="bg-slate-900/60 dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-white dark:text-white mb-4 flex items-center gap-2">
          <Zap className="w-6 h-6 text-yellow-500" />
          Actions to Improve Your Score
        </h2>
        <div className="space-y-4">
          {healthData?.improvement_actions?.map((action, index) => (
            <div 
              key={index}
              className="flex items-center justify-between p-4 bg-slate-900/60 dark:bg-gray-700/50 rounded-lg hover:bg-slate-900/60 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              data-testid={`improvement-action-${index}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold">
                  {index + 1}
                </div>
                <div>
                  <p className="font-medium text-white dark:text-white">{action.action}</p>
                  <p className="text-sm text-slate-400 dark:text-gray-400">
                    +{action.potential_score_improvement} points improvement
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-emerald-600 font-bold">${action.potential_savings?.toLocaleString()}</p>
                <p className="text-sm text-slate-300">potential savings</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    </div>
  );
};

export default CostHealthScore;
